import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { Check, X } from "lucide-react";

const AnimatedBlock = ({ children, delay = 0 }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-60px" });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, delay, ease: "easeOut" }}
    >
      {children}
    </motion.div>
  );
};

const forYouItems = [
  "Hai gi\u00e0 fatto lavoro su di te, ma una parte rimane bloccata nell'ombra",
  "Senti la tua luce\u2026 ma non riesce a stabilizzarsi",
  "Senti che hai dei doni, ma qualcosa ti trattiene quando stai per espanderti",
  "Non ti accontenti: sai che c'\u00e8 molto di pi\u00f9",
  "Vuoi attraversare le tue ombre per liberarne la forza",
  "Senti che la tua energia merita di essere piena, integra, incarnata",
  "Vuoi vivere con presenza, coraggio, verit\u00e0",
  "Senti che sei qui per uno scopo \u2014 e non vuoi pi\u00f9 rimandarlo",
];

const notForYouItems = [
  "Non \u00e8 per chi cerca una soluzione lampo o la \u201cpillola magica\u201d che cambi tutto in poche settimane, senza un lavoro costante su di s\u00e8.",
  "Non \u00e8 per chi preferisce credere che siano \u201cgli altri\u201d o le circostanze l'unico problema e non ha voglia di prendersi la sua parte di responsabilit\u00e0 nel cambiamento.",
  "Non \u00e8 per chi vuole stare comodo, cerca cambiamento ma non \u00e8 disposto a mettersi in discussione, a rivedere abitudini e modi di pensare.",
];

const ForWhomSection = () => {
  return (
    <section
      data-testid="for-whom-section"
      className="py-24 md:py-32 bg-[#ECEAE6]"
    >
      <div className="max-w-5xl mx-auto px-6 lg:px-12">
        <AnimatedBlock>
          <div className="text-center mb-16">
            <h2 className="font-heading text-4xl md:text-5xl font-semibold tracking-tight text-[#442F23] mb-4">
              Per chi &egrave;?
            </h2>
            <p className="font-body text-base md:text-lg text-[#442F23]/70 max-w-3xl mx-auto">
              Light Workers House &egrave; la casa dei Guerrieri di Luce in cammino. Anime profonde, spirituali ma concrete, coraggiose, che sentono la chiamata ad andare oltre la vita ordinaria, a vivere nel pieno delle loro potenzialit&agrave; portando la loro luce nel mondo.
            </p>
          </div>
        </AnimatedBlock>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* For You */}
          <AnimatedBlock delay={0.1}>
            <div className="bg-[#FBF8E9] border border-[#AC8E60]/15 rounded-[2rem] p-8 lg:p-10">
              <h3 className="font-heading text-2xl font-semibold text-[#442F23] mb-6">
                &Egrave; PER TE SE:
              </h3>
              <ul className="space-y-4">
                {forYouItems.map((item, i) => (
                  <li key={i} className="flex items-start gap-3" data-testid={`for-you-item-${i}`}>
                    <Check size={18} className="text-[#E1C340] flex-shrink-0 mt-1" />
                    <span className="font-body text-sm text-[#442F23]/80 leading-relaxed">
                      {item}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </AnimatedBlock>

          {/* Not For You */}
          <AnimatedBlock delay={0.2}>
            <div className="bg-[#FBF8E9] border border-[#AC8E60]/10 rounded-[2rem] p-8 lg:p-10">
              <h3 className="font-heading text-2xl font-semibold text-[#442F23] mb-6">
                Non &egrave; per tutti:
              </h3>
              <ul className="space-y-4">
                {notForYouItems.map((item, i) => (
                  <li key={i} className="flex items-start gap-3" data-testid={`not-for-you-item-${i}`}>
                    <X size={18} className="text-[#AC8E60]/50 flex-shrink-0 mt-1" />
                    <span className="font-body text-sm text-[#442F23]/65 leading-relaxed">
                      {item}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </AnimatedBlock>
        </div>

        {/* Decision CTA */}
        <AnimatedBlock delay={0.3}>
          <div className="mt-16 text-center">
            <p className="font-heading text-2xl md:text-3xl font-semibold text-[#442F23] mb-4">
              &Egrave; per te che hai deciso: <span className="text-[#AC8E60]">di vivere davvero.</span>
            </p>
            <p className="font-body text-base text-[#442F23]/70 max-w-2xl mx-auto leading-relaxed mb-8">
              Di smettere di autosabotarti e limitarti reprimendo la tua essenza.
              Di riprendere in mano la tua vita a 360&deg;, far emergere il tuo potenziale
              e costruire una quotidianit&agrave; fatta di abitudini e mindset che sostengano
              la visione di vita che desideri, non solo i doveri.
            </p>
            <button
              onClick={() => document.getElementById("pricing-section")?.scrollIntoView({ behavior: "smooth" })}
              className="btn-primary text-base"
              data-testid="forwhom-cta"
            >
              Voglio Iscrivermi
            </button>
          </div>
        </AnimatedBlock>
      </div>
    </section>
  );
};

export default ForWhomSection;
